import { Row } from '@/shared/types/api';

export type Notification = Omit<
  Row<'notifications'>,
  'metadata' | 'content_category'
> & {
  is_read: boolean;
  content_category: NotificationContentCategory | null;
  metadata: NotificationMetadata;
};

export enum NotificationType {
  'alert' = 'alert',
  'info' = 'info',
  'custom' = 'custom',
}

export enum NotificationContentCategory {
  'member_management' = 'member_management',
  'project_information' = 'project_information',
  'budget' = 'budget',
  'alert' = 'alert',
  'anomaly' = 'anomaly',
  'tracking' = 'tracking',
}

export enum NotificationContentType {
  'new_org_member' = 'new_org_member',
  'new_project_member' = 'new_project_member',
  'project_end_hint' = 'project_end_hint',
  'project_deadline_exceeded' = 'project_deadline_exceeded',
  'tracking_request_sent' = 'tracking_request_sent',
  'tracking_request_received' = 'tracking_request_received',
  'kpi_breakpoint' = 'kpi_breakpoint',
  'kpi_budget_exceeded' = 'kpi_budget_exceeded',
  'tracking_overdue' = 'tracking_overdue',
  'contractor_invitation_accepted' = 'contractor_invitation_accepted',
  'contractor_invitation' = 'contractor_invitation',
  'client_project_deleted' = 'client_project_deleted',
  'contractor_project_deleted' = 'contractor_project_deleted',
}

export const contentTypeToCategory: Record<
  NotificationContentType,
  NotificationContentCategory
> = {
  [NotificationContentType.new_org_member]:
    NotificationContentCategory.member_management,
  [NotificationContentType.new_project_member]:
    NotificationContentCategory.member_management,
  [NotificationContentType.project_end_hint]:
    NotificationContentCategory.project_information,
  [NotificationContentType.project_deadline_exceeded]:
    NotificationContentCategory.alert,
  [NotificationContentType.tracking_request_sent]:
    NotificationContentCategory.tracking,
  [NotificationContentType.tracking_request_received]:
    NotificationContentCategory.tracking,
  [NotificationContentType.kpi_breakpoint]: NotificationContentCategory.budget,
  [NotificationContentType.kpi_budget_exceeded]:
    NotificationContentCategory.budget,
  [NotificationContentType.tracking_overdue]:
    NotificationContentCategory.tracking,
  [NotificationContentType.contractor_invitation_accepted]:
    NotificationContentCategory.member_management,
  [NotificationContentType.contractor_invitation]:
    NotificationContentCategory.member_management,
  [NotificationContentType.client_project_deleted]:
    NotificationContentCategory.project_information,
  [NotificationContentType.contractor_project_deleted]:
    NotificationContentCategory.project_information,
};

interface BaseNotificationMetadata {
  redirect_url?: string;
}

export interface OrgMemberNotificationMetadata extends BaseNotificationMetadata {
  member_id: string;
  user_name: string;
  role: string;
}

export interface ProjectMemberNotificationMetadata extends BaseNotificationMetadata {
  project_name: string;
  user_name: string;
  project_id: number;
}

export interface ProjectEndHintNotificationMetadata extends BaseNotificationMetadata {
  project_id: number;
  project_name: string;
}

export interface ProjectDeadlineExceededNotificationMetadata extends BaseNotificationMetadata {
  project_id: number;
  project_name: string;
}

export interface TrackingRequestSentNotificationMetadata extends BaseNotificationMetadata {
  client_project_id: number;
  contractor_project_name: string;
}

export interface TrackingRequestReceivedNotificationMetadata extends BaseNotificationMetadata {
  client_org_name: string;
  contractor_subproject_id: number;
  contractor_project_name: string;
}
export interface KpiBreakpointNotificationMetadata extends BaseNotificationMetadata {
  kpi_id: number;
  kpi_name: string;
  project_id: number;
  project_name: string;
  current_percentage: number;
  breakpoint_percentage: number;
}

export interface KpiBudgetExceededNotificationMetadata extends BaseNotificationMetadata {
  kpi_id: number;
  kpi_name: string;
  project_id: number;
  project_name: string;
  current_percentage: number;
}

export interface ContractorInvitationAcceptedNotificationMetadata extends BaseNotificationMetadata {
  contractor_org_name: string;
  project_id: number;
  project_name: string;
}

export interface TrackingOverdueNotificationMetadata extends BaseNotificationMetadata {
  project_id: number;
  project_name: string;
}

export interface ContractorInvitationNotificationMetadata extends BaseNotificationMetadata {
  inviter_org_id: number;
  inviter_org_name: string;
  project_name: string;
  invitation_token: string;
  contractor_org_id: number;
  isNewPartner: boolean;
}

export interface ClientProjectDeletedNotificationMetadata extends BaseNotificationMetadata {
  client_org_name: string | null;
  contractor_project_id: number;
  contractor_project_name: string;
}

export interface ContractorProjectDeletedNotificationMetadata extends BaseNotificationMetadata {
  contractor_org_name: string | null;
  client_project_id: number;
  client_project_name: string;
}

export type NotificationMetadata =
  | OrgMemberNotificationMetadata
  | ProjectMemberNotificationMetadata
  | ProjectEndHintNotificationMetadata
  | ProjectDeadlineExceededNotificationMetadata
  | TrackingRequestSentNotificationMetadata
  | TrackingRequestReceivedNotificationMetadata
  | KpiBreakpointNotificationMetadata
  | KpiBudgetExceededNotificationMetadata
  | TrackingOverdueNotificationMetadata
  | ContractorInvitationAcceptedNotificationMetadata
  | ContractorInvitationNotificationMetadata
  | ClientProjectDeletedNotificationMetadata
  | ContractorProjectDeletedNotificationMetadata;

export type NotificationGroup = {
  date: string;
  notifications: Notification[];
};
